/* @flow */

import type {
  SortableProductList,
  FilterableProductList as TFilterableProductList,
  FilterLocation,
  ProductSort,
} from "shop-state/types";
import type { BreadcrumbLink } from "@crossroads/ui-components";

import React from "react";
import { useTranslate } from "@awardit/react-use-translate";
import Wrapper from "components/Wrapper";
import { useUi } from "helpers/ui";
import ProductList from "components/ProductList";
import PaginatedProductList from "components/PaginatedProductList";
import { PAGE_SIZE } from "effects/route";
import {
  OffCanvasFilterMenu,
  Filterbar,
  StickBelowHeader,
  useFilter,
} from "@crossroads/ui-components";
import ChevronIcon from "icons/chevron.svg";
import CloseIcon from "icons/cross.svg";

import styles from "./styles.scss";

type Props = {
  children?: React$Node,
  updating: boolean,
  productList: SortableProductList | TFilterableProductList,
  load: FilterLocation => any,
  breadcrumbLinks?: $ReadOnlyArray<BreadcrumbLink>,
  category?: string,
};

type InnerProps = {
  updating: boolean,
  productList: SortableProductList | TFilterableProductList,
  load: FilterLocation => any,
  sortValues?: Array<ProductSort>,
};

const FilterableProductListInner = ({ updating, productList, load, sortValues }: InnerProps) => {
  const { filterOCMOpen, setFilterOCMOpen } = useUi();
  const filterConfig = {
    loading: updating,
    productList,
    usePoints: false,
    load,
    sortValues,
  };
  const filterState = useFilter(filterConfig);

  return (
    <>
      <OffCanvasFilterMenu
        isOpen={filterOCMOpen}
        close={() => setFilterOCMOpen(false)}
        filterState={filterState}
        ChevronIcon={ChevronIcon}
        CloseIcon={CloseIcon}
        side="RIGHT"
      />

      <Filterbar
        filterState={filterState}
        openFilters={() => setFilterOCMOpen(true)}
      />
    </>
  );
};

const BLACKLISTED_SORT_VALUES = new Set(["position"]);

const useGetSortValues = (productList: SortableProductList | TFilterableProductList) => {
  const t = useTranslate();

  if (!productList.sortableBy) {
    return [];
  }

  const sortableBy = productList.sortableBy
    .filter(sb => !BLACKLISTED_SORT_VALUES.has(sb.code));

  const sortValues = [
    {
      code: ``,
      label: t("FILTER.FILTERBAR.SORT_DEFAULT"),
    },
  ];

  for (const value of sortableBy) {
    sortValues.push({
      code: `${value.code}_asc`,
      label: `${value.label} ↑`,
    }, {
      code: `${value.code}_desc`,
      label: `${value.label} ↓`,
    });
  }

  return sortValues;
};

const FilterableProductList = ({
  children,
  updating,
  productList,
  load,
  breadcrumbLinks,
  category }: Props): React$Node => {
  const numPages = Math.ceil(productList.totalCount / PAGE_SIZE);
  const sortValues = useGetSortValues(productList);

  const categoryTitle = category ? category : (sortValues.length > 0 && !category ? sortValues[0].label : "");

  return (
    <>
      <FilterableProductListInner
        productList={productList}
        sortValues={sortValues}
        numPages={numPages}
        updating={updating}
        load={load}
      />
      {children}
      <Wrapper>
        <PaginatedProductList
          updating={updating}
          numPages={numPages}
          productList={productList}
          breadcrumbLinks={breadcrumbLinks}
          category={categoryTitle}
        />
      </Wrapper>
    </>
  );
};

export const FilterableProductListHint = ({ children }: { children?: React$Node }): React$Node => (
  <Wrapper>
    <StickBelowHeader className={styles.filterbar}>
      <div className={styles.filterBarHint} />
    </StickBelowHeader>
    {children}
    <ProductList products={[null, null, null, null, null, null, null, null]} />
  </Wrapper>
);

export default FilterableProductList;
